import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { createGlobalStyle } from 'styled-components';
import Icon from 'components/icons';
import {
  getFirstBusinessDayOfNextMonth,
  getFirstBusinessDayOfNextQuarter,
  getNextMonday,
} from 'utils/helpers';
import { DropdownContainer } from '../styles';
import { format } from 'date-fns';
import RadioPopoverDropdown, {
  type RadioPopoverOption,
} from 'components/dropdown/RadioPopoverDropdown';

const formatDate = (date: Date) => {
  return format(date, 'MMMM d, yyyy');
};

const { Text } = Typography;

const FrequencyDropdownStyles = createGlobalStyle`
  .frequency-popover .ant-popover-arrow {
    display: none !important;
  }
`;

const nextMonday = formatDate(getNextMonday());
const firstBusinessDayOfNextMonth = formatDate(getFirstBusinessDayOfNextMonth());
const firstBusinessDayOfNextQuarter = formatDate(getFirstBusinessDayOfNextQuarter());
const options = [
  { value: 'weekly', label: 'Weekly', date: nextMonday },
  { value: 'biweekly', label: 'Bi-weekly', date: nextMonday },
  { value: 'monthly', label: 'Monthly', date: firstBusinessDayOfNextMonth },
  {
    value: 'quarterly',
    label: 'Quarterly',
    date: firstBusinessDayOfNextQuarter,
  },
];

export const getLabelFromValue = (value: string): string => {
  const found = options.find((opt) => opt.value === value);
  return found ? found.label : value;
};

const FrequencyDropdown: React.FC<{
  value: string;
  width?: string | number;
  editIconOutside?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ value, width, disabled, onChange, editIconOutside }) => {
  const getLabelFromValue = (value: string) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const dropdownOptions = useMemo<RadioPopoverOption[]>(
    () =>
      options.map((opt) => ({
        value: opt.value,
        label: <Text>{opt.label}</Text>,
        meta: <div style={{ fontSize: 12, color: 'var(--perfect-gray)' }}>Next: {opt.date}</div>,
      })),
    []
  );

  return (
    <>
      <FrequencyDropdownStyles />
      <RadioPopoverDropdown
        value={value}
        options={dropdownOptions}
        onChange={onChange}
        title="Update delivery frequency"
        actions={{ size: 'small' }}
        disabled={disabled}
        popoverProps={{
          placement: 'bottomLeft',
          overlayClassName: 'frequency-popover',
          overlayInnerStyle: {
            borderRadius: 10,
            boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.10)',
            padding: '8px 4px',
          },
          overlayStyle: { padding: 0 },
        }}
        renderTrigger={({ open, onOpen }) => (
          <DropdownContainer
            size="small"
            style={{ backgroundColor: !open && 'transparent', width }}
            focused={open}
            tabIndex={0}
            $editIconOutside={editIconOutside}
          >
            <Icon
              size="small"
              icon="Edit2"
              onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                onOpen();
              }}
            />
            <Text>{getLabelFromValue(value)}</Text>
          </DropdownContainer>
        )}
      />
    </>
  );
};

export default FrequencyDropdown;
